/**
 * CONCENTRAÇÃO — valor E unidade, ou nenhum dos dois.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ O backend recusa com 422 a apresentação que traz só metade
 *  (`CriarApresentacaoDto`, XOR entre `concentracaoValor` e
 *  `concentracaoUnidade`). A regra vive AQUI DE NOVO, e não por desconfiança
 *  do servidor: "30" sem "mg" não é uma concentração meio preenchida, é uma
 *  informação que não quer dizer nada — e descobrir isso por um erro vermelho
 *  depois de salvar é pior do que ser avisado enquanto se digita.
 *
 *  A concentração é o que transforma "1 comprimido" em dose comparável entre
 *  pacientes. Sem ela, a Análise Comparativa não consegue somar mg/kg.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Teto do DTO (`@maximum 1000000`). */
export const CONCENTRACAO_MAXIMA = 1_000_000;

/**
 * Aceita vírgula: o admin brasileiro digita "2,5", não "2.5".
 *
 * Mesma decisão do `paraNumero` da grade de faixas (catálogo de exames); não é
 * importado de lá porque aquele arquivo é o rascunho DAQUELA grade, e um
 * import atravessando features acoplaria as duas telas por uma linha.
 */
export function paraNumero(texto: string): number | null {
  const limpo = texto.trim().replace(',', '.');
  if (!limpo) return null;
  const valor = Number(limpo);
  return Number.isFinite(valor) ? valor : null;
}

/** Vírgula na exibição, como o campo espera receber de volta. */
export function paraTexto(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return '';
  return String(valor).replace('.', ',');
}

export interface ProblemaDeConcentracao {
  campo: 'concentracaoValor' | 'concentracaoUnidade';
  mensagem: string;
}

/**
 * O que impede este par de virar uma apresentação. `null` = pode salvar.
 *
 * Devolve o CAMPO junto da mensagem para o formulário marcar o input certo —
 * um erro no rodapé dizendo "concentração inválida" com dois campos na tela
 * não diz qual dos dois consertar.
 */
export function problemaDaConcentracao(
  valorDigitado: string,
  unidadeDigitada: string,
): ProblemaDeConcentracao | null {
  const valor = valorDigitado.trim();
  const unidade = unidadeDigitada.trim();

  if (!valor && !unidade) return null;

  if (valor && !unidade) {
    return {
      campo: 'concentracaoUnidade',
      mensagem: 'Informe a unidade: concentração é valor e unidade, ou nenhum dos dois.',
    };
  }

  if (!valor && unidade) {
    return {
      campo: 'concentracaoValor',
      mensagem: 'Informe o valor: concentração é valor e unidade, ou nenhum dos dois.',
    };
  }

  const numero = paraNumero(valor);

  if (numero === null) {
    return { campo: 'concentracaoValor', mensagem: 'Use um número. Ex.: 30 ou 2,5.' };
  }

  if (numero <= 0) {
    return { campo: 'concentracaoValor', mensagem: 'A concentração precisa ser maior que zero.' };
  }

  if (numero > CONCENTRACAO_MAXIMA) {
    return {
      campo: 'concentracaoValor',
      mensagem: `A concentração precisa ser menor que ${CONCENTRACAO_MAXIMA.toLocaleString('pt-BR')}.`,
    };
  }

  return null;
}

/**
 * Prévia do `rotulo` que o servidor vai devolver ("Vetoryl 30 mg").
 *
 * É esse rótulo que aparece no seletor do receituário, e é ele que separa uma
 * apresentação da outra. Mostrá-lo enquanto se digita evita cadastrar
 * "Vetoryl" e "Vetoryl" — duas linhas indistinguíveis na hora de prescrever.
 */
export function previaDoRotulo(
  nomeComercial: string,
  valorDigitado: string,
  unidadeDigitada: string,
): string {
  const nome = nomeComercial.trim();
  const valor = valorDigitado.trim();
  const unidade = unidadeDigitada.trim();

  if (!nome) return '';
  if (!valor || !unidade) return nome;

  return `${nome} ${valor} ${unidade}`;
}
